import { Contact } from '../types/database';

/**
 * Fixes one-way relationships by ensuring all relationships are bidirectional
 * @param contacts Array of all contacts
 * @param updateContact Function to update a contact
 * @returns Promise<void>
 */
export async function fixAllBidirectionalRelationships(
  contacts: Contact[],
  updateContact: (id: string, updates: Partial<Contact>) => Promise<{ data: Contact | null; error: any }>
): Promise<void> {
  console.log('Starting to fix all bidirectional relationships...');
  
  const updates: Array<{ contactId: string; updates: Partial<Contact> }> = [];
  
  // For each contact, find contacts that reference them but they don't reference back
  for (const contact of contacts) {
    const contactsThatReferenceThis = contacts.filter(c => 
      c.id !== contact.id && 
      c.associated_contacts.includes(contact.id) && 
      !contact.associated_contacts.includes(c.id)
    );
    
    if (contactsThatReferenceThis.length > 0) {
      console.log(`Contact ${contact.name} has ${contactsThatReferenceThis.length} one-way relationships to fix`);
      
      // Add the missing contacts to this contact's associated_contacts
      const updatedAssociatedContacts = [...contact.associated_contacts, ...contactsThatReferenceThis.map(c => c.id)];
      const updatedRelationshipTypes = { ...contact.relationship_types };
      
      // Add relationship types for the new contacts
      contactsThatReferenceThis.forEach(c => {
        updatedRelationshipTypes[c.id] = 'Associate'; // Default relationship type
      });
      
      updates.push({
        contactId: contact.id,
        updates: {
          associated_contacts: updatedAssociatedContacts,
          relationship_types: updatedRelationshipTypes,
        }
      });
    }
  }
  
  console.log(`Found ${updates.length} contacts that need relationship fixes`);
  
  // Apply all updates
  for (const update of updates) {
    try {
      const { error } = await updateContact(update.contactId, update.updates);
      if (error) {
        console.error(`Failed to update contact ${update.contactId}:`, error);
      } else {
        console.log(`Successfully updated contact ${update.contactId}`);
      }
    } catch (err) {
      console.error(`Error updating contact ${update.contactId}:`, err);
    }
  }
  
  console.log('Finished fixing bidirectional relationships');
}
